import { memo, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import {
  accessTokenFactory,
  teamChatSignalRConnection,
  teamChatSignalRController,
} from 'app/services/signalR';
import PropTypes from 'prop-types';

import { SIGNAL_R_TEAM_CHAT_CONNECTION_URL } from 'constants/api';
import { TeamChatConnectionStatus } from 'constants/teamChat';
import { logger } from 'utils/logger';

import { setConnectionStatusAction } from 'store/teamChat/actions';

const teamChatInit = { promise: null };

const runTeamChatConnectionInit = async dispatch => {
  try {
    teamChatSignalRConnection.init(SIGNAL_R_TEAM_CHAT_CONNECTION_URL, accessTokenFactory);

    await teamChatSignalRConnection.start();

    teamChatSignalRController.init(dispatch);
    teamChatSignalRController.startListeners();

    teamChatSignalRConnection.onreconnecting(() => {
      dispatch(setConnectionStatusAction(TeamChatConnectionStatus.RECONNECTING));
    });

    teamChatSignalRConnection.onreconnected(() => {
      dispatch(setConnectionStatusAction(TeamChatConnectionStatus.CONNECTED));
      teamChatSignalRController.rejoinAllChannels();
    });

    teamChatSignalRConnection.onclose(() => {
      dispatch(setConnectionStatusAction(TeamChatConnectionStatus.DISCONNECTED));
    });

    dispatch(setConnectionStatusAction(TeamChatConnectionStatus.CONNECTED));
  } catch (error) {
    logger.error('[TeamChat SignalR] Failed to initialize connection', error);
    dispatch(setConnectionStatusAction(TeamChatConnectionStatus.DISCONNECTED));
    teamChatInit.promise = null;
  }
};

const initTeamChatConnection = dispatch => {
  if (teamChatInit.promise) {
    return teamChatInit.promise;
  }

  if (teamChatSignalRConnection.isConnected) {
    teamChatSignalRController.init(dispatch);
    dispatch(setConnectionStatusAction(TeamChatConnectionStatus.CONNECTED));
    teamChatInit.promise = Promise.resolve();

    return teamChatInit.promise;
  }

  dispatch(setConnectionStatusAction(TeamChatConnectionStatus.CONNECTING));

  teamChatInit.promise = runTeamChatConnectionInit(dispatch);

  return teamChatInit.promise;
};

const TeamChatConnector = memo(({ children }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    initTeamChatConnection(dispatch);
  }, [dispatch]);

  return children ?? null;
});
TeamChatConnector.propTypes = {
  children: PropTypes.node,
};

export default TeamChatConnector;
